/**
 * The workspace: which client (tenant), which projects and which sites the
 * signed-in person can see, and which site they are working on.
 *
 * The current site is the one in the URL (`/s/:siteId/…`), so a link to a
 * page of site B opens site B, whatever was selected before. Outside site
 * pages (home, people, knowledge) it is the last site the person opened,
 * remembered in this browser only as a convenience.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import type { AccessibleTenant, SiteSummary } from '@cms/contracts'
import { useMatch, useNavigate } from 'react-router-dom'
import { ApiError, isTenantSelectionError } from '../api/client.js'
import { probePlatformOperator } from '../api/access.js'
import { getProjectTree, listSites, listTenants } from '../api/endpoints.js'
import { readSelectedTenant, saveSelectedTenant } from '../api/tenant-selection.js'
import { projectPaths } from '../domain/site-groups.js'
import { currentTenant, selectionFor, sortTenants } from '../domain/tenants.js'
import { useI18n } from '../i18n/context.js'
import { paths } from '../routes.js'

interface SiteContextValue {
  sites: SiteSummary[]
  /** The site in the URL, or the remembered one; '' when the person has no site. */
  siteId: string
  site: SiteSummary | null
  /** True on `/s/:siteId/…` pages. */
  siteInUrl: boolean
  /** Project id → readable path ("Cliente › Marketing"). Empty when the tree is not readable. */
  projectNames: ReadonlyMap<string, string>
  /** The client every call runs against (home, or the one picked with `X-Tenant`); null when `GET /tenants` said nothing. */
  tenant: AccessibleTenant | null
  /** Every client `GET /tenants` lists, home first; an operator also sees the ones not selectable. */
  tenants: AccessibleTenant[]
  /** Picks another client: remembered in this browser, then sites and screens reload. */
  selectTenant: (tenantId: string) => void
  /** Bumped at every change of client: screens keyed on it start again from nothing. */
  tenantEpoch: number
  /** Set when the remembered client refused the person and the workspace went back home. */
  tenantNotice: string | null
  /** True for a platform operator (PLATFORM_OPERATORS); null while unknown. Hides, never authorizes. */
  operator: boolean | null
  loading: boolean
  error: string | null
  reload: () => Promise<void>
}

const SiteContext = createContext<SiteContextValue | null>(null)
const STORAGE_KEY = 'cms.workspace.siteId'

function readRememberedSite(): string {
  try {
    return window.localStorage.getItem(STORAGE_KEY) ?? ''
  } catch {
    return ''
  }
}

export function rememberedSiteId(): string | null {
  return readRememberedSite() || null
}

/** Every site the person can see: the list is paginated at 100, and a client can have more. */
async function listAllSites(): Promise<SiteSummary[]> {
  const items: SiteSummary[] = []
  let cursor: string | undefined
  for (let page = 0; page < 20; page += 1) {
    const result = await listSites({ limit: 100, cursor })
    items.push(...result.items)
    if (!result.cursor || result.cursor === cursor) break
    cursor = result.cursor
  }
  return items
}

export function SiteProvider({ children }: { children: ReactNode }) {
  const { t } = useI18n()
  const match = useMatch('/s/:siteId/*')
  const urlSiteId = match?.params.siteId ?? ''
  const [sites, setSites] = useState<SiteSummary[]>([])
  const [remembered, setRemembered] = useState(readRememberedSite)
  const [projectNames, setProjectNames] = useState<ReadonlyMap<string, string>>(new Map())
  const [tenants, setTenants] = useState<AccessibleTenant[]>([])
  const [tenantEpoch, setTenantEpoch] = useState(0)
  const [tenantNotice, setTenantNotice] = useState<string | null>(null)
  const [operator, setOperator] = useState<boolean | null>(null)
  const operatorProbed = useRef(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const navigate = useNavigate()

  const reload = useCallback(async () => {
    setLoading(true)
    const loadAll = () => Promise.all([
      listAllSites(),
      // The names are a courtesy: a role that cannot read the tree still works, with fewer words.
      getProjectTree().catch(() => null),
      listTenants().catch(() => [] as AccessibleTenant[]),
    ])
    try {
      let result: Awaited<ReturnType<typeof loadAll>>
      try {
        result = await loadAll()
      } catch (err) {
        // The client remembered here is not this person's any more (a
        // membership revoked, storage edited): back to home, said out loud,
        // rather than every screen failing with the same 403.
        if (!isTenantSelectionError(err) || !readSelectedTenant()) throw err
        saveSelectedTenant(null)
        setTenantNotice(err instanceof ApiError ? err.message : null)
        setTenantEpoch((epoch) => epoch + 1)
        result = await loadAll()
      }
      const [siteItems, tree, tenantItems] = result
      setSites(siteItems)
      setProjectNames(tree ? projectPaths(tree.items) : new Map())
      setTenants(sortTenants(tenantItems))
      setError(null)
      if (!operatorProbed.current && tenantItems.length) {
        operatorProbed.current = true
        // Only an operator is listed tenants it cannot pick; without one of
        // those, one cheap operator-only call answers (403 = no).
        const home = tenantItems.find((entry) => entry.access === 'home') ?? tenantItems[0]!
        setOperator(tenantItems.some((entry) => entry.access === 'operator') || await probePlatformOperator(home.id))
      } else if (!tenantItems.length) {
        // `GET /tenants` said nothing: no panel for now, asked again at the next reload.
        setOperator((known) => known ?? false)
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('errors.network'))
    } finally {
      setLoading(false)
    }
  }, [t])

  useEffect(() => {
    void reload()
  }, [reload])

  const selectTenant = useCallback((tenantId: string) => {
    const selection = selectionFor(tenants, tenantId)
    if (selection === undefined) return
    if (selection === readSelectedTenant() && currentTenant(tenants)?.id === tenantId) return
    saveSelectedTenant(selection)
    setTenantNotice(null)
    setSites([])
    setTenantEpoch((epoch) => epoch + 1)
    // Sites, contents and people belong to the client they came from: the
    // page of a site of the old client would only answer 404.
    if (urlSiteId) navigate(paths.home())
    void reload()
  }, [tenants, urlSiteId, navigate, reload])

  useEffect(() => {
    if (!urlSiteId || !sites.some((site) => site.id === urlSiteId)) return
    setRemembered(urlSiteId)
    try {
      window.localStorage.setItem(STORAGE_KEY, urlSiteId)
    } catch {
      // A preference, not state: losing it only means the home page asks again.
    }
  }, [urlSiteId, sites])

  const value = useMemo<SiteContextValue>(() => {
    const fallback = sites.some((site) => site.id === remembered) ? remembered : (sites[0]?.id ?? '')
    const siteId = urlSiteId || fallback
    return {
      sites,
      siteId,
      site: sites.find((site) => site.id === siteId) ?? null,
      siteInUrl: !!urlSiteId,
      projectNames,
      tenant: currentTenant(tenants),
      tenants,
      selectTenant,
      tenantEpoch,
      tenantNotice,
      operator,
      loading,
      error,
      reload,
    }
  }, [sites, remembered, urlSiteId, projectNames, tenants, selectTenant, tenantEpoch, tenantNotice, operator, loading, error, reload])

  return <SiteContext.Provider value={value}>{children}</SiteContext.Provider>
}

export function useSiteWorkspace(): SiteContextValue {
  const value = useContext(SiteContext)
  if (!value) throw new Error('useSiteWorkspace must be used inside SiteProvider.')
  return value
}
